import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons.js';
import { StatusPill, TechAvatar, fmtTime } from '../components/shell.js';
import { clients, jobs, techs, NOW, STATUS_FLOW, type JobStatus } from '../data/seed.js';

function getWeekDays(anchor: Date): Date[] {
  const d = new Date(anchor);
  const dow = d.getDay();
  const start = new Date(d);
  start.setDate(d.getDate() - dow);
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    return day;
  });
}

const STATUS_KEY: Record<JobStatus, string> = {
  New: 's-new',
  Scheduled: 's-scheduled',
  'In Progress': 's-progress',
  'Waiting Parts': 's-waiting',
  Completed: 's-completed',
  'Follow-up': 's-completed',
  Cancelled: 's-waiting',
};

function statusToColorKey(s: JobStatus): string {
  return s === 'New'
    ? 'new'
    : s === 'Scheduled'
    ? 'scheduled'
    : s === 'In Progress'
    ? 'progress'
    : s === 'Waiting Parts'
    ? 'waiting'
    : s === 'Completed'
    ? 'completed'
    : s === 'Follow-up'
    ? 'followup'
    : 'cancel';
}

export function CalendarPage() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(new Date(NOW));
  const [techFilter, setTechFilter] = useState<string>('all');
  const [selectedDay, setSelectedDay] = useState(new Date(NOW));
  const clientById = Object.fromEntries(clients.map((c) => [c.id, c]));
  const techById = Object.fromEntries(techs.map((t) => [t.id, t]));
  const days = getWeekDays(anchor);
  const HOURS = Array.from({ length: 11 }, (_, i) => i + 8); // 8am–6pm

  const visibleJobs = jobs.filter((j) => techFilter === 'all' || j.techId === techFilter);

  const goPrev = () => {
    const d = new Date(anchor);
    d.setDate(d.getDate() - 7);
    setAnchor(d);
  };
  const goNext = () => {
    const d = new Date(anchor);
    d.setDate(d.getDate() + 7);
    setAnchor(d);
  };
  const goToday = () => setAnchor(new Date(NOW));

  const monthLabel = days[0]!.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const dayJobs = (date: Date) =>
    visibleJobs
      .filter((j) => {
        const d = new Date(j.scheduledStart);
        return d.toDateString() === date.toDateString();
      })
      .sort(
        (a, b) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime(),
      );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Calendar</h1>
          <p>{monthLabel} · week view</p>
        </div>
        <div className="page-header-actions">
          <button className="btn sm" onClick={goPrev}>
            {Icon.chevronLeft({ size: 14 })}
          </button>
          <button className="btn sm" onClick={goToday}>
            Today
          </button>
          <button className="btn sm" onClick={goNext}>
            {Icon.chevronRight({ size: 14 })}
          </button>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={techFilter}
            onChange={(e) => setTechFilter(e.target.value)}
          >
            <option value="all">All techs</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <button className="btn primary">{Icon.plus({ size: 14 })} New job</button>
        </div>
      </div>

      {/* Status legend */}
      <div className="row-flex wrap" style={{ gap: 12, marginBottom: 14, fontSize: 12 }}>
        {[...STATUS_FLOW, 'Cancelled' as JobStatus].map((s) => {
          const ck = statusToColorKey(s);
          return (
            <div key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: `hsl(var(--status-${ck}) / 0.5)`,
                  borderLeft: `3px solid hsl(var(--status-${ck}))`,
                }}
              />
              <span className="muted">{s}</span>
            </div>
          );
        })}
      </div>

      {/* Desktop week grid */}
      <div className="hidden-mobile">
        <div className="cal-week">
          <div className="cal-h" />
          {days.map((d, i) => {
            const isToday = d.toDateString() === new Date(NOW).toDateString();
            return (
              <div key={i} className={'cal-h ' + (isToday ? 'today' : '')}>
                {d.toLocaleDateString('en-US', { weekday: 'short' })}
                <span className="day-num">{d.getDate()}</span>
              </div>
            );
          })}
          {HOURS.map((h) => (
            <Fragment key={h}>
              <div className="cal-time">
                {((h + 11) % 12) + 1}
                {h < 12 ? 'a' : 'p'}
              </div>
              {days.map((d, di) => {
                const evs = visibleJobs.filter((j) => {
                  const jd = new Date(j.scheduledStart);
                  return jd.toDateString() === d.toDateString() && jd.getHours() === h;
                });
                return (
                  <div key={di} className="cal-cell">
                    {evs.map((j) => {
                      const c = clientById[j.clientId]!;
                      const tech = techById[j.techId];
                      return (
                        <div
                          key={j.id}
                          className={'cal-event ' + STATUS_KEY[j.status]}
                          onClick={() => navigate('/jobs/' + j.id)}
                        >
                          <div className="ev-time">
                            {fmtTime(j.scheduledStart)}–{fmtTime(j.scheduledEnd)}
                          </div>
                          <div
                            style={{
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {c.name}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              marginTop: 2,
                            }}
                          >
                            {tech && <TechAvatar tech={tech} size="sm" />}
                            <span
                              style={{
                                fontSize: 10,
                                color: 'hsl(var(--muted-foreground))',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {j.appliance.type}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
        <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
          Drag events to reschedule (visual demo only).
        </div>
      </div>

      {/* Mobile day list */}
      <div className="only-mobile">
        <div
          style={{
            display: 'flex',
            gap: 6,
            overflowX: 'auto',
            marginBottom: 14,
            paddingBottom: 4,
          }}
        >
          {days.map((d, i) => {
            const isSelected = d.toDateString() === selectedDay.toDateString();
            const isToday = d.toDateString() === new Date(NOW).toDateString();
            const count = dayJobs(d).length;
            return (
              <button
                key={i}
                onClick={() => setSelectedDay(d)}
                style={{
                  flex: '0 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '10px 14px',
                  border:
                    '1px solid ' + (isSelected ? 'hsl(var(--foreground))' : 'hsl(var(--border))'),
                  background: isSelected ? 'hsl(var(--foreground))' : 'hsl(var(--card))',
                  color: isSelected ? 'hsl(var(--background))' : 'hsl(var(--foreground))',
                  borderRadius: 10,
                  minWidth: 56,
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    fontSize: 10.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    opacity: 0.8,
                  }}
                >
                  {d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3)}
                </span>
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 2,
                    color: isSelected ? 'inherit' : isToday ? 'hsl(var(--primary))' : 'inherit',
                  }}
                >
                  {d.getDate()}
                </span>
                <span style={{ fontSize: 10.5, marginTop: 2, opacity: 0.75 }}>{count}j</span>
              </button>
            );
          })}
        </div>
        <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600 }}>
          {selectedDay.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })}
        </div>
        <div className="card-list">
          {dayJobs(selectedDay).length === 0 && (
            <div className="empty-state">No jobs scheduled.</div>
          )}
          {dayJobs(selectedDay).map((j) => {
            const c = clientById[j.clientId]!;
            const tech = techById[j.techId];
            const ck = statusToColorKey(j.status);
            return (
              <div
                className="list-card"
                key={j.id}
                onClick={() => navigate('/jobs/' + j.id)}
                style={{ borderLeft: `3px solid hsl(var(--status-${ck}))` }}
              >
                <div className="list-card-row">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'hsl(var(--muted-foreground))',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {fmtTime(j.scheduledStart)} – {fmtTime(j.scheduledEnd)}
                    </div>
                    <div className="list-card-title" style={{ marginTop: 2 }}>
                      {c.name}
                    </div>
                    <div className="list-card-sub">
                      {j.appliance.type} · {j.appliance.brand}
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 6,
                    }}
                  >
                    <StatusPill status={j.status} />
                    {tech && <TechAvatar tech={tech} size="sm" />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
